import { darkTheme, ITheme, lightTheme, ThemeName } from '@sapiens/insured-component-library';
import { DeepRequired, Theme, ThemeBreakpointValues, ThemeMode, ThemeUnit } from './theme';
import { DefaultTheme } from 'styled-components';
import { AT_MEDIA, MEDIA_WIDTH, THEME_DEFAULTS, THEME_OVERRIDES } from '../constants/theme.constants';
import { DarkThemeColors, LightThemeColors, NeutralColors, ProductColors } from '../constants/colors.constant';

const mediaGenerator = (value: string, optional?: string): string =>
  `${AT_MEDIA} (${value})${optional ? ' and (' + optional + ')' : ''}`;

export const generateTheme = (
  mode: ThemeMode = THEME_DEFAULTS.MODE,
  unit: ThemeUnit = THEME_DEFAULTS.UNIT
): DefaultTheme => {
  return {
    ...getCustomisedTheme(mode),
    unit: unit,
    fontFamily: THEME_DEFAULTS.FONT_FAMILY,
    spacingFactor: THEME_DEFAULTS.SPACING_FACTOR,
    zIndex: THEME_DEFAULTS.Z_INDEX,
    customColors: THEME_DEFAULTS.LKF_COLORS,
  };
};

export const spacing = (size: number): string => {
  return `${size * THEME_DEFAULTS.SPACING_FACTOR}${THEME_DEFAULTS.UNIT}`;
};

const getCustomisedTheme = (mode: ThemeMode) => {
  const libTheme = mode === 'light' ? customTheme : darkTheme;

  return mergeDeep(libTheme, {
    components: THEME_OVERRIDES(libTheme as unknown as DefaultTheme, spacing),
  }) as unknown as DeepRequired<Theme>;
};

type BreakPointName = keyof ThemeBreakpointValues;
export const MediaBreakpoints = {
  values: THEME_DEFAULTS.BREAKPOINTS,
  down(breakpoint: BreakPointName): string {
    return mediaGenerator(`${MEDIA_WIDTH.MAX_WIDTH}: ${this.values[breakpoint] - 1}px`);
  },
  up(breakpoint: BreakPointName): string {
    return mediaGenerator(`${MEDIA_WIDTH.MIN_WIDTH}: ${this.values[breakpoint]}px`);
  },
  between(startBreakpoint: BreakPointName, endBreakpoint: BreakPointName): string {
    return mediaGenerator(
      `${MEDIA_WIDTH.MIN_WIDTH}: ${this.values[startBreakpoint]}px`,
      `${MEDIA_WIDTH.MAX_WIDTH}: ${this.values[endBreakpoint] - 1}px`
    );
  },
};

// This is the only way available to customize Theme components
interface IObject {
  [key: string]: any;
}

interface IDeepMerge {
  (target: IObject, ...sources: Array<IObject>): IObject;
}

const isObject = (obj: any) => {
  return obj && obj.constructor === Object;
};

const mergeDeep: IDeepMerge = (target: IObject, ...sources: Array<IObject>): IObject => {
  if (!sources.length) {
    return target;
  }

  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, {
            [key]: {},
          });
        }

        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, {
          [key]: source[key as keyof Partial<ITheme>],
        });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

export const customTheme: Theme = {
  name: ThemeName.LIGHT,
  fontFamily: 'Eina 03',
  colors: {
    primary: LightThemeColors.N900,
    secondary: LightThemeColors.N800,
    tertiary: LightThemeColors.N700,
  },
  palette: {
    product: ProductColors,
    light: LightThemeColors,
    dark: DarkThemeColors,
    neutral: NeutralColors,
  },
  elevation: lightTheme.elevation,
  components: lightTheme.components,
};
